import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Routes,
  Route,
  Navigate,
  useParams,
  useNavigate,
  BrowserRouter,
} from "react-router-dom";

import ClientRoutes from "./ClientRoutes";
import AuthRoutes from "./AuthRoutes";
import AdminRoutes from "./AdminRoutes";
import Error404 from "../pages/errors/Error404";
import ProtectedRoute from "../components/common/ProtectedRoute";
import type { RootState, AppDispatch } from "../store";
import { changeLanguage } from "../store/language/languageSlice";
import { DEFAULT_LANGUAGE, isAcceptableLanguage } from "../constants/languages";

// Component to handle language-based routing and sync
const LanguageRouter = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { lang } = useParams<{ lang: string }>();
  const { currentLanguage } = useSelector((state: RootState) => state.language);

  useEffect(() => {
    if (!lang) return;

    // If URL language is not acceptable, redirect to default
    if (!isAcceptableLanguage(lang)) {
      navigate(`/${DEFAULT_LANGUAGE}`, { replace: true });
      return;
    }

    // If URL language differs from Redux state, update Redux
    if (lang !== currentLanguage) {
      dispatch(changeLanguage(lang));
    }
  }, [lang, currentLanguage, dispatch, navigate]);

  // If language is not valid yet, don't render
  if (!lang || !isAcceptableLanguage(lang)) {
    return null;
  }

  return (
    <Routes>
      {/* Client routes - default */}
      <Route path="/*" element={<ClientRoutes />} />
      {/* Auth routes */}
      <Route path="/auth/*" element={<AuthRoutes />} />
      {/* Protected admin routes */}
      {/* AdminRoutes includes: dashboard, users, projects, events, announcements, businesses, attractions, blogs, settings, profile, change-password */}
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute>
            <AdminRoutes />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

const AppRoutes = () => {
  const { currentLanguage } = useSelector((state: RootState) => state.language);

  return (
    <BrowserRouter>
      <Routes>
        {/* Client routes - default for language paths */}
        <Route path="/:lang/*" element={<LanguageRouter />} />

        {/* Default redirect to current language */}
        <Route
          path="/"
          element={<Navigate to={`/${currentLanguage}`} replace />}
        />

        {/* Catch all - 404 */}
        <Route path="*" element={<Error404 />} />
      </Routes>
    </BrowserRouter>
  );
};

export default AppRoutes;
